<script setup lang="ts">
import { computed } from 'vue'
import AppCard from '../../primitives/AppCard/AppCard.vue'
import AppButton from '../../primitives/AppButton/AppButton.vue'
import AppTextDiff from '../../primitives/AppTextDiff/AppTextDiff.vue'
import type { AgentAskModel } from '../../../lib/chat/agent-ask'

const props = withDefaults(
  defineProps<{
    /** Everything the card draws, built by one of `lib/chat/agent-ask`'s adapters. */
    ask: AgentAskModel
    pending?: boolean
  }>(),
  { pending: false },
)

const emit = defineEmits<{
  choose: [id: string]
}>()

const SETTLED_TONE = {
  positive: 'text-accent-green',
  neutral: 'text-text-tertiary',
  negative: 'text-accent-red',
} as const

const settledToneClass = computed(() =>
  props.ask.settled ? SETTLED_TONE[props.ask.settled.tone] : undefined,
)
</script>

<template>
  <AppCard dense class="flex flex-col gap-6">
    <div v-if="ask.settled" :class="['flex items-center gap-4 text-body-sm', settledToneClass]">
      <component :is="ask.settled.icon" class="shrink-0" aria-hidden="true" />
      <span>{{ ask.settled.message }}</span>
    </div>

    <template v-else>
      <p v-if="ask.prompt" class="text-body-sm whitespace-pre-wrap text-text-primary">
        {{ ask.prompt }}
      </p>

      <div v-if="ask.identity" class="flex items-center gap-8">
        <div
          :class="[
            'flex size-32 shrink-0 items-center justify-center rounded-3',
            ask.identity.toneClass,
          ]"
        >
          <component :is="ask.identity.icon" aria-hidden="true" />
        </div>
        <div class="flex min-w-0 flex-col gap-1">
          <p class="text-body-sm-emphasized text-text-primary">{{ ask.identity.name }}</p>
          <p class="text-caption text-text-tertiary">{{ ask.identity.caption }}</p>
        </div>
      </div>

      <AppTextDiff
        v-if="ask.preview"
        :before="ask.preview.before"
        :after="ask.preview.after"
        max-height="sm"
      />

      <p v-if="ask.error" class="text-caption text-accent-red">{{ ask.error }}</p>

      <div v-if="ask.choices.length" class="flex flex-wrap items-center gap-4">
        <AppButton
          v-for="choice in ask.choices"
          :key="choice.id"
          :variant="choice.variant"
          :label="choice.label"
          :start-icon="choice.icon"
          :loading="choice.busy"
          :is-inactive="pending"
          @click="emit('choose', choice.id)"
        />
      </div>

      <p v-if="ask.footnote" class="text-caption text-text-tertiary">{{ ask.footnote }}</p>
    </template>
    <span v-if="ask.settled" class="sr-only">{{ ask.settled.announcement }}</span>
  </AppCard>
</template>
